import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  DollarSign, Send, RefreshCcw, CheckCircle2, ChevronDown, ChevronRight,
  Globe, Mail, ExternalLink, History,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import AdminLayout from '@/components/admin/AdminLayout';
import { toast } from 'sonner';
import { useTwoFactorPrompt, callWith2FA } from '@/components/TwoFactorPrompt';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const fmtUsd = (n) =>
  Number(n || 0).toLocaleString('en-US', { style: 'currency', currency: 'USD' });
const fmtDate = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString('en-US', {
      year: 'numeric', month: 'short', day: 'numeric',
    });
  } catch { return iso.slice(0, 10); }
};

function AdminManualPayouts() {
  const [tab, setTab] = useState('pending'); // 'pending' | 'history'
  const [rail, setRail] = useState(''); // '' | 'manual_wise' | 'stripe_global'
  const [pending, setPending] = useState({ creators: [], summary: {} });
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState({}); // creator_id -> bool

  // Mark-paid modal state
  const [paying, setPaying] = useState(null); // creator bucket being paid
  const [wiseRef, setWiseRef] = useState('');
  const [wiseAmount, setWiseAmount] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [triggering, setTriggering] = useState(null); // creator_id firing global
  const [testingPayout, setTestingPayout] = useState(null); // payout_id firing single test
  const { promptFor2FA, TwoFactorPromptModal } = useTwoFactorPrompt();

  const loadPending = useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const params = rail ? `?rail=${rail}` : '';
      const res = await axios.get(`${API_URL}/api/admin/manual-payouts/pending${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setPending(res.data || { creators: [], summary: {} });
    } catch (e) {
      toast.error('Failed to load pending payouts');
    } finally {
      setLoading(false);
    }
  }, [rail]);

  const loadHistory = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API_URL}/api/admin/manual-payouts/history`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setHistory(res.data?.items || []);
    } catch (e) {
      toast.error('Failed to load history');
    }
  }, []);

  useEffect(() => {
    if (tab === 'pending') loadPending();
    else loadHistory();
  }, [tab, loadPending, loadHistory]);

  const openPayModal = (bucket) => {
    setPaying(bucket);
    setWiseRef('');
    setWiseAmount(bucket.pending_total.toFixed(2));
  };

  const submitPayment = async () => {
    if (!paying) return;
    if (!wiseRef.trim()) {
      toast.error('Wise reference required');
      return;
    }
    try {
      setSubmitting(true);
      const token = localStorage.getItem('token');
      await axios.post(`${API_URL}/api/admin/manual-payouts/mark-paid`, {
        payout_ids: paying.items.map((i) => i.payout_id),
        wise_reference: wiseRef.trim(),
        payout_amount: wiseAmount ? parseFloat(wiseAmount) : null,
      }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success(`Marked ${paying.items.length} payouts paid`);
      setPaying(null);
      await loadPending();
    } catch (e) {
      toast.error('Failed to mark paid');
    } finally {
      setSubmitting(false);
    }
  };

  const triggerStripeGlobal = async (bucket) => {
    try {
      setTriggering(bucket.creator_id);
      const token = localStorage.getItem('token');
      const res = await callWith2FA(
        (extraHeaders = {}) => axios.post(
          `${API_URL}/api/admin/global-payouts/trigger`,
          { payout_ids: bucket.items.map((i) => i.payout_id) },
          { headers: { Authorization: `Bearer ${token}`, ...extraHeaders } }
        ),
        promptFor2FA
      );
      const enabled = res.data?.global_payouts_enabled;
      const succeeded = (res.data?.results || []).filter((r) => r.status === 'ok').length;
      if (!enabled) {
        toast.warning(
          'Stripe Global Payouts not yet enabled — set STRIPE_GLOBAL_PAYOUTS_ENABLED=true after Stripe approves your account',
          { duration: 6000 }
        );
      } else if (succeeded > 0) {
        toast.success(`${succeeded} payouts sent via Stripe Outbound Payments`);
      } else {
        toast.error('Stripe API rejected the payouts — check logs');
      }
      await loadPending();
    } catch (e) {
      if (e.cancelled) return; // user dismissed 2FA modal
      toast.error('Trigger failed');
    } finally {
      setTriggering(null);
    }
  };

  // Fire a SINGLE payout row as an isolated sanity-check of the Stripe
  // Global Payouts integration. Useful before letting the monthly cron
  // batch-fire every creator's accruals — diagnoses recipient / payout-
  // method setup for one creator without touching everyone else's queue.
  const triggerSinglePayoutTest = async (bucket, item) => {
    const ok = window.confirm(
      `Send a single test payout?\n\n` +
      `Creator: ${bucket.creator_name || bucket.creator_id}\n` +
      `Amount: ${fmtUsd(item.amount)}\n` +
      `Order: ${item.order_id}\n\n` +
      `This fires ONE Stripe Outbound Payment using payout_id as the ` +
      `idempotency key (safe to retry). Real money will move if Stripe ` +
      `Global Payouts is enabled. Proceed?`
    );
    if (!ok) return;
    try {
      setTestingPayout(item.payout_id);
      const token = localStorage.getItem('token');
      const res = await callWith2FA(
        (extraHeaders = {}) => axios.post(
          `${API_URL}/api/admin/global-payouts/trigger`,
          { payout_ids: [item.payout_id] },
          { headers: { Authorization: `Bearer ${token}`, ...extraHeaders } }
        ),
        promptFor2FA
      );
      const enabled = res.data?.global_payouts_enabled;
      const result = (res.data?.results || [])[0] || {};
      if (!enabled) {
        toast.warning(
          'Stripe Global Payouts not yet enabled — dry-run only. Set STRIPE_GLOBAL_PAYOUTS_ENABLED=true after Stripe approves you.',
          { duration: 6000 }
        );
      } else if (result.status === 'ok') {
        toast.success(
          `Test payout sent · OutboundPayment ${result.outbound_payment_id || ''}`,
          { duration: 6000 }
        );
      } else {
        toast.error(`Test payout failed: ${result.status || 'unknown error'}`);
      }
      await loadPending();
    } catch (e) {
      if (e.cancelled) return; // user dismissed 2FA modal
      toast.error(e.response?.data?.detail || 'Test payout failed');
    } finally {
      setTestingPayout(null);
    }
  };

  const toggleExpand = (cid) => setExpanded((e) => ({ ...e, [cid]: !e[cid] }));

  return (
    <AdminLayout>
      <div className="mx-auto max-w-6xl space-y-6 p-4 md:p-8">
        <div className="flex flex-col gap-2 md:flex-row md:items-end md:justify-between">
          <div>
            <h1 className="text-3xl font-bold text-white" data-testid="manual-payouts-heading">
              International Payouts
            </h1>
            <p className="mt-1 max-w-2xl text-sm text-white/60">
              Creators in countries Stripe Connect doesn't reach are paid via
              one of two rails: <strong className="text-[#635BFF]">Stripe Global Payouts</strong>{' '}
              (auto-fires once your Stripe account is approved) or{' '}
              <strong className="text-amber-400">Manual Wise</strong> (admin-sent transfer,
              legacy/edge-case escape hatch).
            </p>
          </div>
          <Button
            data-testid="manual-payouts-refresh"
            variant="outline"
            onClick={() => (tab === 'pending' ? loadPending() : loadHistory())}
            className="border-white/20 bg-transparent text-white hover:bg-white/5"
          >
            <RefreshCcw className="mr-2 h-4 w-4" />
            Refresh
          </Button>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 rounded-lg border border-white/10 bg-neutral-900 p-1">
          <button
            data-testid="manual-payouts-tab-pending"
            onClick={() => setTab('pending')}
            className={`flex-1 rounded-md px-4 py-2 text-sm font-medium transition-colors ${
              tab === 'pending'
                ? 'bg-[#00F0FF] text-black'
                : 'text-white/70 hover:text-white'
            }`}
          >
            Pending ({pending.summary?.creators_owed ?? 0})
          </button>
          <button
            data-testid="manual-payouts-tab-history"
            onClick={() => setTab('history')}
            className={`flex-1 rounded-md px-4 py-2 text-sm font-medium transition-colors ${
              tab === 'history'
                ? 'bg-[#00F0FF] text-black'
                : 'text-white/70 hover:text-white'
            }`}
          >
            <History className="mr-1 inline h-3.5 w-3.5" />
            Paid history
          </button>
        </div>

        {/* Rail filter (Pending tab only) */}
        {tab === 'pending' && (
          <div className="flex flex-wrap items-center gap-2 text-xs">
            <span className="text-white/50">Filter rail:</span>
            {[
              { key: '', label: 'All' },
              { key: 'stripe_global', label: 'Stripe Global Payouts' },
              { key: 'manual_wise', label: 'Manual Wise (legacy)' },
            ].map((opt) => (
              <button
                key={opt.key || 'all'}
                data-testid={`rail-filter-${opt.key || 'all'}`}
                onClick={() => setRail(opt.key)}
                className={`rounded-full border px-3 py-1 transition-colors ${
                  rail === opt.key
                    ? 'border-[#00F0FF] bg-[#00F0FF]/15 text-[#00F0FF]'
                    : 'border-white/10 text-white/60 hover:border-white/30'
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>
        )}

        {/* Pending */}
        {tab === 'pending' && (
          <>
            <div className="grid grid-cols-3 gap-4">
              <SummaryCard
                label="Creators owed"
                value={pending.summary?.creators_owed ?? 0}
                accent="text-[#FFB400]"
                testId="payouts-summary-creators"
              />
              <SummaryCard
                label="Total pending"
                value={fmtUsd(pending.summary?.total_pending ?? 0)}
                accent="text-[#00FF94]"
                testId="payouts-summary-total"
              />
              <SummaryCard
                label="Line items"
                value={pending.summary?.total_items ?? 0}
                accent="text-white"
                testId="payouts-summary-items"
              />
            </div>

            {loading ? (
              <Skeleton text="Loading pending payouts…" />
            ) : pending.creators.length === 0 ? (
              <EmptyState
                icon={<DollarSign />}
                title="No pending payouts"
                body="When a manual-Wise creator makes a sale, their share will appear here."
              />
            ) : (
              <div className="space-y-3">
                {pending.creators.map((b) => (
                  <div
                    key={b.creator_id}
                    data-testid={`payout-creator-${b.creator_id}`}
                    className="overflow-hidden rounded-xl border border-white/10 bg-neutral-900"
                  >
                    <div className="flex flex-wrap items-center justify-between gap-3 p-4">
                      <button
                        onClick={() => toggleExpand(b.creator_id)}
                        className="flex flex-1 items-center gap-3 text-left hover:opacity-80"
                      >
                        {expanded[b.creator_id] ? (
                          <ChevronDown className="h-4 w-4 text-white/50" />
                        ) : (
                          <ChevronRight className="h-4 w-4 text-white/50" />
                        )}
                        <div>
                          <div className="font-semibold text-white">
                            {b.creator_name || b.creator_id}
                          </div>
                          <div className="mt-0.5 flex flex-wrap items-center gap-3 text-xs text-white/50">
                            {b.creator_email && (
                              <span className="inline-flex items-center gap-1">
                                <Mail className="h-3 w-3" />
                                {b.creator_email}
                              </span>
                            )}
                            {b.country && (
                              <span className="inline-flex items-center gap-1">
                                <Globe className="h-3 w-3" />
                                {b.country}
                              </span>
                            )}
                            <span>
                              {b.pending_count} order{b.pending_count !== 1 ? 's' : ''}
                            </span>
                          </div>
                        </div>
                      </button>
                      <div className="flex items-center gap-3">
                        <div className="text-right">
                          <div className="text-2xl font-semibold text-[#00FF94]">
                            {fmtUsd(b.pending_total)}
                          </div>
                          <div className="text-xs text-white/40">
                            owed · <RailLabel rail={b.rail} />
                          </div>
                        </div>
                        {b.rail === 'stripe_global' ? (
                          <Button
                            data-testid={`payout-trigger-global-${b.creator_id}`}
                            onClick={() => triggerStripeGlobal(b)}
                            disabled={triggering === b.creator_id}
                            className="bg-[#635BFF] text-white hover:bg-[#635BFF]/90"
                          >
                            {triggering === b.creator_id ? (
                              <RefreshCcw className="mr-2 h-4 w-4 animate-spin" />
                            ) : (
                              <Send className="mr-2 h-4 w-4" />
                            )}
                            Trigger Stripe payout
                          </Button>
                        ) : (
                          <Button
                            data-testid={`payout-mark-paid-${b.creator_id}`}
                            onClick={() => openPayModal(b)}
                            className="bg-[#00F0FF] text-black hover:bg-[#00F0FF]/90"
                          >
                            <Send className="mr-2 h-4 w-4" />
                            Mark paid
                          </Button>
                        )}
                      </div>
                    </div>
                    {expanded[b.creator_id] && (
                      <div className="border-t border-white/5 bg-neutral-950 p-4">
                        {b.wise_email && (
                          <div className="mb-3 text-xs text-white/60">
                            Wise email on file: <code className="rounded bg-white/10 px-1.5 py-0.5 font-mono text-white">{b.wise_email}</code>
                          </div>
                        )}
                        {b.payout_notes && (
                          <div className="mb-3 rounded border border-white/10 bg-black/30 p-2 text-xs text-white/70">
                            <strong>Notes:</strong> {b.payout_notes}
                          </div>
                        )}
                        <table className="w-full text-sm">
                          <thead className="text-xs uppercase tracking-wider text-white/40">
                            <tr>
                              <th className="pb-2 text-left font-medium">Product</th>
                              <th className="pb-2 text-left font-medium">Buyer</th>
                              <th className="pb-2 text-left font-medium">Order</th>
                              <th className="pb-2 text-right font-medium">Amount</th>
                              <th className="pb-2 text-right font-medium">Date</th>
                              {b.rail === 'stripe_global' && (
                                <th className="pb-2 text-right font-medium">Test</th>
                              )}
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-white/5">
                            {b.items.map((it) => (
                              <tr key={it.payout_id} className="text-white/80">
                                <td className="py-2">{it.product_name || '—'}</td>
                                <td className="py-2 text-xs text-white/60">{it.buyer_email}</td>
                                <td className="py-2 font-mono text-xs text-white/60">{it.order_id}</td>
                                <td className="py-2 text-right text-[#00FF94]">{fmtUsd(it.amount)}</td>
                                <td className="py-2 text-right text-xs text-white/50">{fmtDate(it.created_at)}</td>
                                {b.rail === 'stripe_global' && (
                                  <td className="py-2 text-right">
                                    <button
                                      data-testid={`payout-test-single-${it.payout_id}`}
                                      onClick={() => triggerSinglePayoutTest(b, it)}
                                      disabled={testingPayout === it.payout_id}
                                      title="Fire this single Outbound Payment as an integration sanity-check"
                                      className="inline-flex items-center gap-1 rounded-md border border-[#635BFF]/40 bg-[#635BFF]/10 px-2 py-1 text-xs text-[#9D96FF] hover:border-[#635BFF] hover:bg-[#635BFF]/20 disabled:opacity-50 disabled:cursor-wait transition-colors"
                                    >
                                      {testingPayout === it.payout_id ? (
                                        <RefreshCcw className="h-3 w-3 animate-spin" />
                                      ) : (
                                        <Send className="h-3 w-3" />
                                      )}
                                      Test payout
                                    </button>
                                  </td>
                                )}
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </>
        )}

        {/* History */}
        {tab === 'history' && (
          loading ? (
            <Skeleton text="Loading history…" />
          ) : history.length === 0 ? (
            <EmptyState
              icon={<History />}
              title="No payouts yet"
              body="Marked-paid transactions will appear here for your records."
            />
          ) : (
            <div className="overflow-hidden rounded-xl border border-white/10 bg-neutral-950">
              <table className="w-full text-sm" data-testid="manual-payouts-history-table">
                <thead className="bg-neutral-900 text-xs uppercase tracking-wider text-white/50">
                  <tr>
                    <th className="px-4 py-3 text-left font-medium">Creator</th>
                    <th className="px-4 py-3 text-left font-medium">Product</th>
                    <th className="px-4 py-3 text-right font-medium">Amount</th>
                    <th className="px-4 py-3 text-left font-medium">Wise ref</th>
                    <th className="px-4 py-3 text-right font-medium">Paid</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/5">
                  {history.map((p) => (
                    <tr key={p.payout_id} data-testid={`payout-history-${p.payout_id}`}>
                      <td className="px-4 py-3 text-white">{p.creator_name || p.creator_id}</td>
                      <td className="px-4 py-3 text-white/70">{p.product_name}</td>
                      <td className="px-4 py-3 text-right font-medium text-[#00FF94]">{fmtUsd(p.amount)}</td>
                      <td className="px-4 py-3 font-mono text-xs text-white/60">{p.wise_reference}</td>
                      <td className="px-4 py-3 text-right text-xs text-white/50">{fmtDate(p.paid_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )
        )}
      </div>

      {/* Mark-paid modal */}
      <Dialog open={!!paying} onOpenChange={(o) => !o && setPaying(null)}>
        <DialogContent className="bg-neutral-950 text-white">
          <DialogHeader>
            <DialogTitle>Mark {paying?.creator_name || 'creator'} paid</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 text-sm">
            <p className="text-white/60">
              Settling{' '}
              <strong className="text-white">{paying?.pending_count}</strong>{' '}
              line item{(paying?.pending_count || 0) !== 1 ? 's' : ''} totaling{' '}
              <strong className="text-[#00FF94]">{fmtUsd(paying?.pending_total)}</strong>.
              Paste the Wise transfer reference once you've sent the payment.
            </p>
            <div>
              <label className="mb-1 block text-xs uppercase tracking-wider text-white/50">
                Wise reference / transaction id
              </label>
              <Input
                data-testid="mark-paid-wise-ref"
                value={wiseRef}
                onChange={(e) => setWiseRef(e.target.value)}
                placeholder="e.g. TRANSFER-123456789"
                className="bg-neutral-900 border-white/10 text-white"
              />
            </div>
            <div>
              <label className="mb-1 block text-xs uppercase tracking-wider text-white/50">
                Actual amount sent (USD, optional)
              </label>
              <Input
                data-testid="mark-paid-amount"
                type="number"
                step="0.01"
                value={wiseAmount}
                onChange={(e) => setWiseAmount(e.target.value)}
                placeholder="Auto-filled with owed amount"
                className="bg-neutral-900 border-white/10 text-white"
              />
              <p className="mt-1 text-xs text-white/40">
                FX or Wise fees may differ from the platform amount — record what
                you actually sent for the audit trail.
              </p>
            </div>
            <a
              href="https://wise.com/transfers"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-xs text-[#00F0FF] hover:underline"
            >
              Open Wise transfers <ExternalLink className="h-3 w-3" />
            </a>
          </div>
          <DialogFooter className="flex-col-reverse gap-2 sm:flex-row">
            <Button
              variant="outline"
              onClick={() => setPaying(null)}
              className="border-white/20 bg-transparent text-white hover:bg-white/5"
            >
              Cancel
            </Button>
            <Button
              data-testid="mark-paid-submit"
              onClick={submitPayment}
              disabled={submitting || !wiseRef.trim()}
              className="bg-[#00FF94] text-black hover:bg-[#00FF94]/90 disabled:opacity-50"
            >
              <CheckCircle2 className="mr-2 h-4 w-4" />
              {submitting ? 'Saving…' : 'Confirm paid'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <TwoFactorPromptModal />
    </AdminLayout>
  );
}

const SummaryCard = ({ label, value, accent, testId }) => (
  <div
    data-testid={testId}
    className="rounded-xl border border-white/10 bg-neutral-900 p-4"
  >
    <div className="mb-1 text-xs uppercase tracking-wider text-white/50">
      {label}
    </div>
    <div className={`text-2xl font-semibold ${accent}`}>{value}</div>
  </div>
);

const Skeleton = ({ text }) => (
  <div className="rounded-xl border border-white/10 bg-neutral-950 py-12 text-center text-white/50">
    {text}
  </div>
);

const RailLabel = ({ rail }) => {
  if (rail === 'stripe_global') {
    return <span className="text-[#635BFF]">Stripe Global</span>;
  }
  return <span className="text-amber-400">Manual Wise</span>;
};

const EmptyState = ({ icon, title, body }) => (
  <div
    data-testid="manual-payouts-empty"
    className="rounded-xl border border-white/10 bg-neutral-950 p-12 text-center"
  >
    <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-white/5 text-white/40 [&>svg]:h-6 [&>svg]:w-6">
      {icon}
    </div>
    <h3 className="text-lg font-semibold text-white">{title}</h3>
    <p className="mt-1 text-sm text-white/50">{body}</p>
  </div>
);

export default AdminManualPayouts;
